// BattleArena.jsx
import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Send, Loader2, Clock, Zap, Hash, Delete,
  PauseCircle, Gauge, Bot, FileText, Edit, Brain, Target,
} from 'lucide-react';
import { Avatar } from './battleUtils';
import './BattleArena.css';

// ─── helpers ──────────────────────────────────────────────────────────────────
const fmt        = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
const countWords = (t) => t.trim().split(/\s+/).filter(Boolean).length;

// ─── Ghost text renderer ───────────────────────────────────────────────────────
// Only renders shimmer blocks for words typed so far (wordCount), rest is empty
const GhostText = ({ text, wordCount = 0 }) => {
  if (!text || wordCount === 0) return <span className="ba-ghost-empty">Opponent is thinking…</span>;
  const chunks = text.split(/(\s+)/);
  let shown = 0;
  return (
    <span className="ba-ghost-text">
      {chunks.map((chunk, i) => {
        if (!chunk.trim()) {
          // only render spacing if we still have words left to show
          return shown < wordCount ? <span key={i}>{chunk}</span> : null;
        }
        if (shown >= wordCount) return null;
        shown++;
        return (
          <span key={i} className="ba-ghost-block"
            style={{ width: `${Math.max(24, chunk.length * 8)}px` }} />
        );
      })}
    </span>
  );
};

// ─── Stat pill ─────────────────────────────────────────────────────────────────
const StatPill = ({ icon: Icon, label, value, accent = false }) => (
  <div className={`ba-stat-pill ${accent ? 'accent' : ''}`}>
    <Icon size={12} className="ba-stat-icon" />
    <span className="ba-stat-label">{label}</span>
    <span className="ba-stat-value">{value}</span>
  </div>
);

// ─── Timer ─────────────────────────────────────────────────────────────────────
const TimerBar = ({ timeLeft, total = 120 }) => {
  const pct   = Math.max(0, (timeLeft / total) * 100);
  const color = timeLeft > 60 ? '#10B981' : timeLeft > 30 ? '#F59E0B' : '#EF4444';
  return (
    <div className={`ba-timer ${timeLeft <= 30 ? 'urgent' : ''}`}>
      <Clock size={13} style={{ color, flexShrink: 0 }} />
      <div className="ba-timer-track">
        <div className="ba-timer-fill" style={{ width: `${pct}%`, background: color }} />
      </div>
      <span className="ba-timer-num" style={{ color }}>{fmt(timeLeft)}</span>
    </div>
  );
};

// ─── Challenge details block (shown in BOTH panels) ───────────────────────────
const ChallengeDetails = ({ challenge, constraints }) => (
  <div className="ba-challenge-content">

    <h2 className="ba-main-title">
      <Bot className="ba-title-icon" size={22} />
      {challenge.title}
      <span className={`ba-difficulty ${challenge.difficulty?.toLowerCase()}`}>
        {challenge.difficulty || 'Medium'}
      </span>
    </h2>

    {challenge.objective && (
      <div className="ba-section">
        <h5>🎯 Objective:</h5>
        <p>{challenge.objective}</p>
      </div>
    )}

    <div className="ba-section">
      <h5>📝 Task Description:</h5>
      <p>{challenge.description}</p>
    </div>

    {constraints?.length > 0 && (
      <div className="ba-section">
        <h5>⚡ Rules to Follow (Follow all {constraints.length} to score high):</h5>
        <div className="ba-constraints-list">
          {constraints.map((c, i) => (
            <div key={i} className="ba-constraint-item">
              <span className="ba-constraint-number">{i + 1}.</span>
              <span className="ba-constraint-text">{c}</span>
            </div>
          ))}
        </div>
      </div>
    )}

    {challenge.remarks && (
      <div className="ba-section">
        <h5>💡 Additional Notes:</h5>
        <p>{challenge.remarks}</p>
      </div>
    )}
  </div>
);

// ─── MAIN ─────────────────────────────────────────────────────────────────────
const BattleArena = ({
  challenge, constraints, minimumScore, timeLeft,
  myUsername, myEntry, opponent,
  userPrompt, setUserPrompt,
  isSubmitting, myScore, opponentSubmitted, submitBattle,
}) => {

  const [myStats, setMyStats] = useState({ words: 0, wpm: 0, edits: 0, pauses: 0, tokens: 0 });
  const myStatsRef = useRef({ lastLen: 0, editCount: 0, pauseCount: 0, lastChange: Date.now(), startTime: Date.now() });
  const pauseTimer = useRef(null);

  const [ghostProgress, setGhostProgress]   = useState(0);
  const [ghostWordCount, setGhostWordCount] = useState(0);
  const [ghostStats, setGhostStats]         = useState({ words: 0, wpm: 0, edits: 0, pauses: 0, tokens: 0 });
  const ghostRef = useRef({ startTime: Date.now(), edits: 0, pauses: 0, interval: null });

  // Ref for right panel body + workspace section for auto-scroll
  const rightPanelBodyRef   = useRef(null);
  const ghostWorkspaceRef   = useRef(null);
  const hasScrolledOnce     = useRef(false);

  const ghostSeedText = `${challenge.description} ${constraints.join(' ')} Please ensure the response addresses all the key requirements and constraints listed above while maintaining clarity and specificity throughout the prompt structure.`;

  // Ghost progress ticker
  useEffect(() => {
    const totalDuration = 90 + Math.random() * 40;
    const tick = () => {
      const elapsed  = (Date.now() - ghostRef.current.startTime) / 1000;
      const raw      = Math.min(elapsed / totalDuration, 0.95);
      const progress = raw < 0.7 ? raw * 1.1 : 0.77 + (raw - 0.7) * 0.6;
      setGhostProgress(Math.min(progress, 0.95));
      const words = Math.floor(progress * 80 + Math.random() * 3);
      const wpm   = elapsed > 5 ? Math.round((words / elapsed) * 60) : 0;
      if (Math.random() < 0.03) ghostRef.current.pauses++;
      if (Math.random() < 0.05) ghostRef.current.edits++;
      setGhostWordCount(words);
      setGhostStats({ words, wpm, edits: ghostRef.current.edits, pauses: ghostRef.current.pauses, tokens: Math.ceil(words * 1.3) });
    };
    ghostRef.current.interval = setInterval(tick, 800);
    return () => clearInterval(ghostRef.current.interval);
  }, []);

  // When opponent submits, stop ticker (no reveal)
  useEffect(() => {
    if (!opponentSubmitted) return;
    clearInterval(ghostRef.current.interval);
  }, [opponentSubmitted]);

  // Auto-scroll right panel to ghost workspace once ghost starts making progress
  useEffect(() => {
    if (hasScrolledOnce.current) return;
    if (ghostProgress > 0.05 && ghostWorkspaceRef.current && rightPanelBodyRef.current) {
      hasScrolledOnce.current = true;
      setTimeout(() => {
        ghostWorkspaceRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 400);
    }
  }, [ghostProgress]);

  const handlePromptChange = useCallback((e) => {
    const val = e.target.value;
    setUserPrompt(val);
    const ref     = myStatsRef.current;
    const now     = Date.now();
    const newLen  = val.length;
    const elapsed = (now - ref.startTime) / 1000;
    const words   = countWords(val);
    const wpm     = elapsed > 3 ? Math.round((words / elapsed) * 60) : 0;
    if (newLen < ref.lastLen) ref.editCount++;
    ref.lastLen = newLen;
    clearTimeout(pauseTimer.current);
    if (now - ref.lastChange > 1500 && val.length > 0) ref.pauseCount++;
    ref.lastChange = now;
    pauseTimer.current = setTimeout(() => { ref.pauseCount++; }, 2000);
    setMyStats({ words, wpm, edits: ref.editCount, pauses: ref.pauseCount, tokens: Math.ceil(words * 1.3) });
  }, [setUserPrompt]);

  const myWords = countWords(userPrompt);
  const oppName = opponent?.username ?? '???';

  return (
    <div className="ba-two-panels">

      {/* ══════════════════════════════════════════
          LEFT PANEL — Your Workspace
      ══════════════════════════════════════════ */}
      <div className="ba-panel ba-panel-left">

        <div className="ba-column-header">
          <FileText className="ba-column-icon" size={18} />
          <h3>Your Workspace</h3>
          <div className="ba-header-identity">
            <span className="ba-you-dot" />
            <Avatar seed={myUsername} size={20} />
            <span className="ba-identity-name">{myUsername}</span>
            {myScore !== null && (
              <span className="ba-live-score"
                style={{ color: myScore >= (minimumScore || 75) ? '#10B981' : '#EF4444' }}>
                {myScore}/100 ✓
              </span>
            )}
          </div>
          <div className="ba-header-timer">
            <TimerBar timeLeft={timeLeft} total={120} />
          </div>
        </div>

        <div className="ba-panel-body">

          <ChallengeDetails
            challenge={challenge}
            constraints={constraints}
          />

          <div className="ba-workspace-section">
            <div className="ba-workspace-header">
              <Edit className="ba-workspace-icon" size={16} />
              <h3>Prompt Workspace</h3>
            </div>
            <div className="ba-workspace-content">

              <div className="ba-stats-strip">
                <StatPill icon={Hash}        label="Words"  value={myStats.words} />
                <StatPill icon={Zap}         label="WPM"    value={myStats.wpm}   accent={myStats.wpm > 20} />
                <StatPill icon={Delete}      label="Edits"  value={myStats.edits} />
                <StatPill icon={PauseCircle} label="Pauses" value={myStats.pauses} />
                <StatPill icon={Gauge}       label="Tokens" value={`~${myStats.tokens}`} />
              </div>

              {myScore === null ? (
                <>
                  <textarea
                    className="ba-textarea"
                    placeholder="Write your prompt here… Focus on clarity, specificity, and following all constraints."
                    value={userPrompt}
                    onChange={handlePromptChange}
                    disabled={isSubmitting}
                    rows={7}
                    autoFocus
                  />
                  <div className="ba-input-stats">
                    <span>Words: <strong>{myWords}</strong></span>
                    <span>Characters: <strong>{userPrompt.length}</strong></span>
                    <span>Tokens: <strong>~{Math.ceil(myWords * 1.3)}</strong></span>
                  </div>
                  <button
                    className="ba-submit-btn"
                    onClick={submitBattle}
                    disabled={!userPrompt.trim() || isSubmitting}
                  >
                    {isSubmitting
                      ? <><Loader2 size={15} className="ba-spin" /> Analyzing & Testing…</>
                      : <><Send size={15} /> Submit & Test Prompt</>}
                  </button>
                </>
              ) : (
                <div className="ba-submitted-view">
                  <div className="ba-submitted-label">Your submitted prompt</div>
                  <p className="ba-submitted-text">{userPrompt}</p>
                  <div className="ba-waiting-opp">
                    {opponentSubmitted ? '⚡ Both submitted — calculating result…' : '⏳ Waiting for opponent…'}
                  </div>
                </div>
              )}
            </div>
          </div>

        </div>
      </div>

      {/* ══════════════════════════════════════════
          RIGHT PANEL — Opponent's Workspace
      ══════════════════════════════════════════ */}
      <div className="ba-panel ba-panel-right">

        <div className="ba-column-header ba-column-header-right">
          <FileText className="ba-column-icon" size={18} />
          <h3>{oppName}'s Workspace</h3>
          <div className="ba-header-identity">
            <span className="ba-opp-dot" />
            <Avatar seed={opponent?.username} size={20} />
            <span className="ba-identity-name">{oppName}</span>
            {opponentSubmitted
              ? <span className="ba-opp-submitted-tag">Submitted ✓</span>
              : (
                <span className="ba-typing-indicator">
                  <span className="ba-typing-dot" />
                  <span className="ba-typing-dot" />
                  <span className="ba-typing-dot" />
                  typing…
                </span>
              )
            }
          </div>
        </div>

        <div className="ba-panel-body ba-panel-body-right" ref={rightPanelBodyRef}>

          <ChallengeDetails
            challenge={challenge}
            constraints={constraints}
          />

          {/* Ghost workspace — ref for scroll target */}
          <div className="ba-workspace-section" ref={ghostWorkspaceRef}>
            <div className="ba-workspace-header">
              <Brain className="ba-workspace-icon" size={16} />
              {/* Use opponent username */}
              <h3>{oppName}'s Prompt</h3>
            </div>
            <div className="ba-workspace-content">

              <div className="ba-stats-strip ba-stats-opp">
                <StatPill icon={Hash}        label="Words"  value={ghostStats.words} />
                <StatPill icon={Zap}         label="WPM"    value={ghostStats.wpm}   accent={ghostStats.wpm > 20} />
                <StatPill icon={Delete}      label="Edits"  value={ghostStats.edits} />
                <StatPill icon={PauseCircle} label="Pauses" value={ghostStats.pauses} />
                <StatPill icon={Gauge}       label="Tokens" value={`~${ghostStats.tokens}`} />
              </div>

              {/* Progress bar */}
              <div className="ba-ghost-progress-wrap">
                <div className="ba-ghost-progress-fill" style={{ width: `${ghostProgress * 100}%` }} />
              </div>

              <div className="ba-ghost-header-row">
                <span className="ba-ghost-wordcount">{ghostWordCount} words estimated</span>
                {opponentSubmitted && (
                  <span className="ba-reveal-label">✓ Submitted</span>
                )}
              </div>

              {/* Ghost text — shimmer blocks only for typed words so far */}
              <div className="ba-ghost-content">
                <GhostText text={ghostSeedText} wordCount={ghostWordCount} />
              </div>

              <p className="ba-ghost-notice">⚠ Content hidden until both players submit</p>

            </div>
          </div>

        </div>
      </div>

    </div>
  );
};

export default BattleArena;